import React from "react";

/**
 * Stops one broken component from taking a whole screen down.
 *
 * The app had no boundaries at all, which meant a single unguarded property
 * read — an analytics widget reading `data.views.total` when the request came
 * back shaped differently, say — unmounted the entire React tree and left the
 * user staring at a white window with no way back. React's default really is
 * "blank the page"; a boundary is the only thing that changes it.
 *
 * Two use sites, deliberately different:
 *   <ErrorBoundary>                       around a page — offers a reload
 *   <ErrorBoundary variant="inline">      around a widget — keeps the page alive
 */
class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { error: null };
	}

	static getDerivedStateFromError(error) {
		return { error };
	}

	componentDidCatch(error, info) {
		// Desktop keeps a real log file; this is what support will ask for.
		// eslint-disable-next-line no-console
		console.error("[ErrorBoundary]", this.props.label || "unlabelled", error, info?.componentStack);
	}

	render() {
		const { error } = this.state;
		if (!error) return this.props.children;

		if (this.props.variant === "inline") {
			return (
				<div
					style={{
						padding: "14px 16px",
						background: "#fef2f2",
						border: "1px solid #fecaca",
						borderRadius: 10,
						color: "#991b1b",
						fontSize: 13,
					}}
				>
					This section couldn’t be displayed. The rest of the page is unaffected.
				</div>
			);
		}

		return (
			<div
				style={{
					minHeight: "60vh",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					gap: 12,
					padding: 24,
					textAlign: "center",
				}}
			>
				<div style={{ fontSize: 34 }}>😕</div>
				<h2 style={{ margin: 0, fontSize: 20, color: "#0f172a" }}>Something went wrong</h2>
				<p style={{ margin: 0, color: "#64748b", fontSize: 14, maxWidth: 420 }}>
					This screen hit an unexpected error. Your data is safe — reloading usually
					clears it.
				</p>
				<button
					onClick={() => window.location.reload()}
					style={{
						marginTop: 4,
						background: "#14b8a6",
						color: "#fff",
						border: "none",
						borderRadius: 8,
						padding: "9px 18px",
						fontSize: 14,
						fontWeight: 600,
						cursor: "pointer",
					}}
				>
					Reload
				</button>
			</div>
		);
	}
}

export default ErrorBoundary;
